import React, { useMemo } from 'react';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import { ascending } from 'd3';
import Link from 'next/link';
import GrowingStats from '@/components/landing/GrowingStats';
import MapCarousel from '@/components/landing/MapCarousel';
import { H2, H3, H5, P } from '@/components/shadcn/typography';
import { Button } from '@/components/shadcn/button';
import CoverageMap from '@/components/ui/map/CoverageMap';
import LazyTransition from '@/components/ui/shared/LazyTransition';
import HivemapperPin from '@/components/ui/icons/logo/HivemapperPin';
import PhoneWallet from '@/public/images/landing/phone-wallet.png';
import DriveRewardsBg from '@/public/images/landing/drive-rewards-bg.jpg';
import BeeAngled from '@/public/images/landing/bee-angled.png';
import BeeAngledMobile from '@/public/images/landing/bee-angled-mobile.png';
import { Card, CardContent, CardTitle } from '@/components/shadcn/card';
import {
  externalUrls,
  HIVEMAPPER_DOCS_URL,
  routes,
  socialUrls,
} from '@/constants/urls';
import SocialPostCarousel from '@/components/landing/SocialPostCarousel';
import { socialPostsBottom, socialPostsTop } from '@/constants/landing';
import XIcon from '@/components/ui/icons/XIcon';
import Layout from '@/components/layout/Layout';
import AreaChart from '@/components/ui/charts/AreaChart';
import useExplorerData from '@/hooks/useExplorerData';
import { IAreaGraph } from '@/types/explorer';
import PaddedAnchor from '@/components/ui/shared/PaddedAnchor';

export enum SectionIds {
  Hero = 'hero',
  BuiltByThousands = 'built-by-thousands',
  Honey = 'honey',
  Rewards = 'rewards',
  SocialBuzz = 'social-buzz',
}

const scrollToSection = (sectionId: SectionIds) => {
  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth' });
  }
};

function Page() {
  const { data } = useExplorerData();
  const anchorTopPadding = 'calc(-1 * var(--header-height)';

  const graphData = useMemo(() => {
    let uniqueKmMappedData: IAreaGraph[] = [];
    let totalKmMappedData: IAreaGraph[] = [];
    let totalHoneyRewardsData: IAreaGraph[] = [];
    const stats = data?.stats;

    if (stats) {
      uniqueKmMappedData = Object.entries(stats?.byDay)
        .map(([date, value]) => ({
          date,
          value: value.totalUniqueKm,
        }))
        .sort((a, b) => ascending(a.date, b.date));

      totalKmMappedData = Object.entries(stats?.byDay)
        .map(([date, value]) => ({
          date,
          value: value.totalKm,
        }))
        .sort((a, b) => ascending(a.date, b.date));

      totalHoneyRewardsData = Object.entries(stats?.byDay)
        .map(([date, value]) => ({
          date,
          value: value.totalRewardTransactions,
        }))
        .sort((a, b) => ascending(a.date, b.date));
    }

    return {
      uniqueKmMappedData,
      totalKmMappedData,
      totalHoneyRewardsData,
    };
  }, [data]);

  return (
    <Layout>
      {/* Hero Section */}
      <LazyTransition animationClassName="animate-fadeIn">
        <section className="flex w-full max-w-xl flex-col items-center gap-8 px-3 pb-10 pt-2 md:pb-20 md:pt-5 lg:gap-8 lg:px-0">
          <div className="flex w-full flex-col gap-3 md:gap-8 lg:items-center lg:p-0 lg:px-4">
            <H3 className="text-left font-[600] lg:text-center">
              Map the World.
              <br />
              Do It Together.
            </H3>
            <P className="max-w-[400px] text-left text-neutral-300 lg:text-center">
              Hivemapper is a decentralized network of people, cameras, and apps
              mapping the world in real time.
            </P>
            <div className="hidden gap-3 md:flex">
              <Link href={routes.coverage}>
                <Button variant="default" size="xl">
                  Explore Coverage
                </Button>
              </Link>
            </div>
          </div>
          <div className="w-full md:w-[880px]">
            <GrowingStats />
          </div>
          <MapCarousel />
          <div className="flex w-full flex-col gap-3 md:hidden">
            <Button
              onClick={() => scrollToSection(SectionIds.BuiltByThousands)}
              variant="default"
              className="w-full"
              size="xl">
              Explore the Network
            </Button>
          </div>
        </section>
      </LazyTransition>

      {/* Built by Thousands */}
      <LazyTransition animationClassName="animate-fadeIn">
        <PaddedAnchor
          topPadding={anchorTopPadding}
          id={SectionIds.BuiltByThousands}
          className="flex w-full max-w-xl flex-col items-center gap-8 px-3 py-10 md:px-0 md:py-20">
          <div className="flex w-full flex-col gap-6 md:items-center lg:p-0 lg:px-4">
            <div className="flex flex-col gap-3 md:items-center md:gap-6">
              <H3 className="text-left font-[600] md:text-center">
                Fastest-Growing
                <br />
                Network of Its Kind.
              </H3>
              <P className="max-w-[550px] text-left text-neutral-300 md:text-center">
                The Hivemapper Network grows with every drive from global
                contributors — explore everywhere we’ve been with our
                interactive coverage map.
              </P>
            </div>
            <div className="hidden gap-3 md:flex">
              <Link href={routes.network.regions}>
                <Button variant="dark" size="xl" outline="darkThin">
                  Explore Regions
                </Button>
              </Link>
              <Link href={routes.coverage}>
                <Button variant="default" size="xl">
                  Explore Coverage
                </Button>
              </Link>
            </div>
          </div>
          <div className="h-[428px] w-full overflow-hidden rounded-lg">
            <CoverageMap
              clickToInteract
              options={{ enableExpandMap: true, rememberLocation: false }}
            />
          </div>
          <div className="flex w-full flex-col gap-6 md:flex-row">
            <Card className="flex size-full flex-col gap-6 p-6">
              <CardTitle className="text-xl font-normal">
                Total Road KM Mapped
              </CardTitle>
              <CardContent className="h-[170px] p-0 md:h-[250px]">
                <AreaChart
                  id="total-km-mapped"
                  data={graphData.totalKmMappedData}
                  yAxisProps={{
                    domain: ['dataMin - 50000000', 'dataMax'],
                    interval: 1,
                  }}
                  showYAxis
                />
              </CardContent>
            </Card>
            <Card className="flex size-full flex-col gap-6 p-6">
              <CardTitle className="text-xl font-normal">
                Unique Road KM Mapped
              </CardTitle>
              <CardContent className="h-[170px] p-0 md:h-[250px]">
                <AreaChart
                  id="unique-km-mapped"
                  fillColor="hsl(var(--azure-700))"
                  yAxisProps={{
                    domain: ['dataMin - 3000000', 'dataMax'],
                    interval: 0,
                  }}
                  data={graphData.uniqueKmMappedData}
                  showYAxis
                />
              </CardContent>
            </Card>
          </div>
        </PaddedAnchor>
      </LazyTransition>

      {/* HONEY Section */}
      <PaddedAnchor
        id={SectionIds.Honey}
        topPadding={anchorTopPadding}
        className="relative flex w-full flex-col items-center bg-neutral-900/50 px-4 py-10 md:px-0 md:py-20">
        <div className="flex w-full max-w-xl flex-col items-center gap-3 md:gap-12">
          <div className="flex w-full flex-col items-center gap-3 md:w-[880px] md:flex-row md:gap-12">
            <LazyTransition animationClassName="animate-fadeIn">
              <div className="flex basis-full flex-col gap-6 md:basis-1/2 md:gap-8">
                <div className="flex items-center gap-2 md:gap-5">
                  <HivemapperPin className="size-[40px] md:size-[74px]" />
                  <H2 className="font-bold">HONEY</H2>
                </div>
                <div className="flex flex-col items-start gap-3 md:gap-6">
                  <H5>
                    The Economic Engine
                    <br /> of the Hivemapper Network
                  </H5>
                  <P>
                    {`The HONEY token rewards contributors who power the
                    Hivemapper Network with fresh map data. Whether you're
                    collecting imagery, your efforts are
                    tracked on-chain and rewarded transparently.`}
                  </P>
                  <Link
                    href={HIVEMAPPER_DOCS_URL}
                    className="w-full md:w-auto"
                    target="_blank">
                    <Button size="xl" className="z-10 w-full md:w-auto">
                      Documentation
                    </Button>
                  </Link>
                </div>
              </div>
            </LazyTransition>
            <LazyTransition animationClassName="animate-slideUp">
              <div
                style={{
                  maskImage:
                    'linear-gradient(180deg, rgba(0, 0, 0, 1) 70%, transparent)',
                }}
                className="flex basis-1/2 items-center justify-center p-4 md:px-5 md:py-0">
                <Image
                  alt="crypto wallet app displayed in smartphone"
                  src={PhoneWallet}
                  className="size-full"
                />
              </div>
            </LazyTransition>
          </div>
        </div>
      </PaddedAnchor>

      {/* Drive Rewards Section */}
      <PaddedAnchor
        id={SectionIds.Rewards}
        topPadding={anchorTopPadding}
        className="relative flex w-full items-center justify-center bg-neutral-900/55 px-6 py-14 md:px-0 md:py-[100px]">
        <div
          className="absolute inset-0 size-full"
          style={{
            maskImage:
              'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 1) 5% 95%, transparent)',
            WebkitMaskImage:
              'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 1) 5% 95%, transparent)',
          }}>
          <Image
            src={DriveRewardsBg}
            className="z-0 size-full object-cover object-center md:object-fill"
            quality={100}
            alt="car windshield"
          />
        </div>
        <LazyTransition
          animationClassName="animate-slideUp"
          animationDuration={800}>
          <Card className="z-10 flex w-full max-w-xl flex-col-reverse gap-6 rounded-md border-neutral-800 bg-neutral-900 p-6 md:flex-row md:gap-12 md:p-8">
            <div className="flex w-full max-w-[324px] flex-col justify-between gap-6 md:gap-0 md:p-2">
              <div className="hidden md:block">
                <P>Drive. Map. Earn.</P>
              </div>
              <div className="flex flex-col gap-6 md:gap-4">
                <H3 className="lg:text-[30px]">
                  Turn Every Drive into Rewards
                </H3>
                <P className="max-w-[500px] text-neutral-300">
                  You’ll contribute to a global map, earn HONEY, and unlock
                  fleet protection with Beekeeper — all passively, with no extra
                  work.
                </P>
              </div>
              <div className="flex w-full flex-col justify-start gap-3 md:flex-row">
                <Link
                  href="https://docs.hivemapper.com/contribute/driving"
                  className="flex w-full items-center justify-center md:w-auto"
                  target="_blank">
                  <Button
                    size="xl"
                    variant="darkLight"
                    outline="darkLightThin"
                    className="w-full md:w-auto">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex size-full flex-col items-center justify-center gap-6 md:py-3">
              <div className="block w-full md:hidden">
                <P>Drive. Map. Earn.</P>
              </div>
              <Image
                src={BeeAngled}
                alt="Bee Device"
                className="hidden size-full md:block"
              />
              <Image
                src={BeeAngledMobile}
                alt="Bee Device"
                className="block size-full md:hidden"
              />
            </div>
          </Card>
        </LazyTransition>
      </PaddedAnchor>

      {/* Social Buzz Section */}
      <LazyTransition animationClassName="animate-fadeIn">
        <PaddedAnchor
          id={SectionIds.SocialBuzz}
          topPadding={anchorTopPadding}
          className="flex w-full flex-col gap-8 px-4 py-10 md:px-0 md:py-20">
          <div className="flex flex-col gap-3 md:items-center md:gap-4">
            <span className="text-2xl">🐝 ♥️</span>
            <H3 className="font-[600] md:text-center">
              The Buzz
              <br />
              Around Hivemapper
            </H3>
            <P className="max-w-[500px] text-neutral-300 md:px-20 md:text-center">
              Real posts from real contributors powering the world’s first
              decentralized mapping network.
            </P>
          </div>
          <div className="flex w-full flex-col gap-6">
            <SocialPostCarousel posts={socialPostsTop} direction="left" />
            <SocialPostCarousel posts={socialPostsBottom} direction="left" />
          </div>
          <div className="hidden items-center justify-center gap-3 md:flex">
            <Link href={socialUrls.x} target="_blank">
              <Button
                className="flex items-center justify-between gap-3"
                variant="dark"
                size="xl"
                outline="darkThin">
                Follow Us <XIcon className="size-4 fill-azure-400" />
              </Button>
            </Link>
          </div>
        </PaddedAnchor>
      </LazyTransition>
    </Layout>
  );
}

export default Page;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const country = req.headers['x-country'] || 'unknown';
  return {
    props: {
      country,
    },
  };
};
